// Package imports
import React, { useEffect, useState } from 'react';
import { Routes, Route } from 'react-router-dom';
import Cookies from 'universal-cookie';

// CSS imports
import './App.css';

// Component imports
import Home from './pages/home/Home';
import Navigation from './components/navigation/Navigation';
import LoginModal from './components/signupLogin/loginModal/LoginModal';
import UserRequests from './pages/userRequests/UserRequests';
import AdminUsers from './pages/adminUsers/AdminUsers';
import EmailTemplates from './pages/emailTemplates/EmailTemplates';
import CreateEmailTemplate from './pages/createEmailTemplate/CreateEmailTemplate';

// Helper imports
import AuthAccessWrapper from './helpers/components/authAccessWrapper/AuthAccessWrapper';

// Service imports
import { ApiService } from './services/ApiService';

// Type imports
import { User } from './types/user/User';
import UpdateEmailTemplate from './pages/updateEmailTemplate/UpdateEmailTemplate';
import BlogPosts from './pages/blogPosts/BlogPosts';
import CreateBlogPost from './pages/createBlogPost/CreateBlogPost';
import UpdateBlogPost from './pages/updateBlogPost/UpdateBlogPost';
import NewsletterEmails from './pages/newsletterEmails/NewsletterEmails';
import CreateNewsletterEmail from './pages/createNewsletterEmail/CreateNewsletterEmail';
import UpdateNewsletterEmail from './pages/updateNewsletterEmail/UpdateNewsletterEmail';
import NewsletterUsers from './pages/newsletterUsers/NewsletterUsers';

const App: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [showLogin, setShowLogin] = useState(false);

  const [isInitialStartup, setIsInitialStartup] = useState(true);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [googleAuthDone, setGoogleAuthDone] = useState(false);

  const toggleLogin = () => {
    setShowLogin(!showLogin);
  };

  const authenticateUser = async () => {
    const cookies = new Cookies();
    const currentRefreshToken = cookies.get('refreshToken');
    if (currentRefreshToken) {
      const loggedUser: User | null = await ApiService.initialAuthenticate(
        currentRefreshToken
      );

      setUser(loggedUser);
    }

    setIsInitialLoading(false);
  };

  useEffect(() => {
    if (isInitialStartup) {
      authenticateUser();
      setIsInitialStartup(false);
    }
  }, [isInitialStartup]);

  return (
    <div className='App'>
      <LoginModal
        showLogin={showLogin}
        googleAuthDone={googleAuthDone}
        setShowLogin={setShowLogin}
        setGoogleAuthDone={setGoogleAuthDone}
        setUser={setUser}
      />
      <AuthAccessWrapper
        user={user}
        isInitialLoading={isInitialLoading}
        toggleLogin={toggleLogin}
      >
        <Navigation user={user} setUser={setUser} toggleLogin={toggleLogin} />
        <main className='full-page-content'>
          <Routes>
            <Route path='/' element={<Home />} />
            <Route path='/user-requests' element={<UserRequests />} />
            <Route
              path='/admin-users'
              element={<AdminUsers loggedInUser={user as User} />}
            />
            <Route path='/email-templates' element={<EmailTemplates />} />
            <Route
              path='/email-template/create'
              element={<CreateEmailTemplate />}
            />
            <Route
              path='/email-template/:id'
              element={<UpdateEmailTemplate />}
            />
            <Route path='/newsletter-emails' element={<NewsletterEmails />} />
            <Route
              path='/newsletter-email/create'
              element={<CreateNewsletterEmail />}
            />
            <Route
              path='/newsletter-email/:id'
              element={<UpdateNewsletterEmail />}
            />
            <Route path='/newsletter-users' element={<NewsletterUsers />} />
            <Route path='/blog' element={<BlogPosts user={user as User} />} />
            <Route path='/blog/create' element={<CreateBlogPost />} />
            <Route path='/blog/:urlTitle/edit' element={<UpdateBlogPost />} />
          </Routes>
        </main>
      </AuthAccessWrapper>
    </div>
  );
};

export default App;
